import { useJsApiLoader } from '@react-google-maps/api';
import { useState, useEffect, createContext, useContext } from 'react';

// const LIBRARIES = ["places", "drawing", "geometry","visualization"];
const LIBRARIES = ["drawing", "places", "geometry", "visualization"];
const constants = require('../constant.js');

const GoogleMapsContext = createContext(null);
const MAX_RETRIES = 3;

export const GoogleMapsProvider = ({ children }) => {
  // Get API key from localStorage or fallback to constants
  const [apiKey, setApiKey] = useState(() => {
    const stored = localStorage.getItem('google_key');
    return stored && stored !== '0' ? stored : constants.API_KEY;
  });

  const [retryCount, setRetryCount] = useState(0);
  const [canRenderLoader, setCanRenderLoader] = useState(false);

  useEffect(() => {
    const key = localStorage.getItem('google_key');
    const validKey = key && key !== '0' ? key : constants.API_KEY;
    setApiKey(validKey);
    setCanRenderLoader(true);
    
    // Function to handle storage changes
    const handleStorageChange = (e) => {
      if (e.key === 'google_key') {
        const newKey = e.newValue && e.newValue !== '0' ? e.newValue : constants.API_KEY;
        if (newKey !== apiKey) {
          setApiKey(newKey);
          // Force reload if key changes to ensure Maps API reinitializes
          window.location.reload();
        }
      }
    };

    // Listen for custom google_key_updated event
    const handleCustomEvent = (e) => {
      const newKey = localStorage.getItem('google_key');
      const validNewKey = newKey && newKey !== '0' ? newKey : constants.API_KEY;
      if (validNewKey !== apiKey) {
        setApiKey(validNewKey);
        // Force reload if key changes to ensure Maps API reinitializes
        window.location.reload();
      }
    };
    
    // Add event listeners
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('google_key_updated', handleCustomEvent);
    
    // Cleanup event listeners
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('google_key_updated', handleCustomEvent);
    };
  }, [apiKey]);

  const loaderState = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: apiKey,
    version: '3.64',
    libraries: LIBRARIES,
    language: 'en',
    region: 'US',
  });

  // Retry logic on failure
  useEffect(() => {
    if (loaderState.loadError && retryCount < MAX_RETRIES) {
      console.warn(`Google Maps failed to load (attempt ${retryCount + 1})`, loaderState.loadError);
      const timer = setTimeout(() => {
        setRetryCount(prev => prev + 1);
        window.location.reload(); // full reload, safest fix to re-init Google Maps
      }, 2000 * (retryCount + 1)); // exponential backoff

      return () => clearTimeout(timer);
    }

    if (loaderState.isLoaded && retryCount > 0) {
      setRetryCount(0);
    }
  }, [loaderState.isLoaded, loaderState.loadError, retryCount]);

  const contextValue = {
    ...loaderState,
    retryCount,
    apiKey
  };

  // Avoid initializing loader until valid key is determined
  if (!canRenderLoader || !apiKey || apiKey === '0') {
    return null; // or show <Loading /> UI
  }

  return (
    <GoogleMapsContext.Provider value={contextValue}>
      {children}
    </GoogleMapsContext.Provider>
  );
};

export const useGoogleMapsLoader = () => {
  const context = useContext(GoogleMapsContext);
  if (!context) {
    console.warn("useGoogleMapsLoader must be used inside a <GoogleMapsProvider>");
    return {
      isLoaded: false,
      loadError: new Error("GoogleMapsProvider missing"),
      retryCount: 0,
    };
  }
  return context;
};

export { LIBRARIES };
